import React, { useEffect, useState } from "react";
import { UserPlus, Loader2, Shield } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getUsers, inviteUser, updateUserRole } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { formatDate } from "../../lib/utils";

const ROLE_VARIANT = { FleetAdmin: "info", ReleaseManager: "success", SupportEngineer: "warning" };
const ROLES = ["FleetAdmin", "ReleaseManager", "SupportEngineer"];

const inviteSchema = z.object({
  name: z.string().min(2, "Name required"),
  email: z.string().email("Valid email required"),
  phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, "Enter a valid phone number (e.g. +6591234567)"),
  role: z.enum(["FleetAdmin", "ReleaseManager", "SupportEngineer"]),
});

export default function UserManagementPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUsers().then((u) => { setUsers(u); setLoading(false); });
  }, []);

  const { register, handleSubmit, setValue, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(inviteSchema),
  });

  const onInvite = async (data) => {
    try {
      const newUser = await inviteUser(data);
      setUsers((prev) => [...prev, { ...newUser, createdAt: new Date().toISOString(), lastLogin: null }]);
      setInviteOpen(false);
      reset();
      toast({ title: "User invited", description: `Invitation sent to ${data.email}`, variant: "success" });
    } catch {
      toast({ title: "Invite failed", variant: "destructive" });
    }
  };

  const handleRoleChange = async (sub, role) => {
    try {
      await updateUserRole(sub, role);
      setUsers((prev) => prev.map((u) => u.sub === sub ? { ...u, role } : u));
      toast({ title: "Role updated", variant: "success" });
    } catch {
      toast({ title: "Role update failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Cognito user pool members. Groups carry the role claim verified server-side by API Gateway.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" /> Invite User
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Change Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>
              )}
              {users.map((u) => (
                <TableRow key={u.sub}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-slate-400" />
                      <Badge variant={ROLE_VARIANT[u.role] || "muted"}>{u.role}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.status === "CONFIRMED" ? "success" : "warning"}>
                      {u.status === "CONFIRMED" ? "Active" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(u.createdAt)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(u.lastLogin)}</TableCell>
                  <TableCell>
                    <Select defaultValue={u.role} onValueChange={(r) => handleRoleChange(u.sub, r)}>
                      <SelectTrigger className="h-8 w-40 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              A temporary password will be sent by Cognito. The user must change it on first login.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onInvite)} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" placeholder="Jane Smith" {...register("name")} />
              {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" type="email" placeholder="jane@tetra.med" {...register("email")} />
              {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-phone">Mobile Number</Label>
              <Input id="inv-phone" type="tel" placeholder="+6591234567" {...register("phone")} />
              {errors.phone && <p className="text-xs text-red-600">{errors.phone.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select onValueChange={(v) => setValue("role", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.role && <p className="text-xs text-red-600">Role is required</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setInviteOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
